import {
  Box,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../src//assets/images/radio-active-icon.svg';
import {ReactComponent as RadioInactiveIcon} from '../../../../src/assets/images/radio-inactive-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../src/assets/images/radio-enable-icon.svg';
import './Analysis.scss';
import {
  useGetAnalysis,
  useGetFolders,
} from '../../../hooks/documentPrinting/useDocumentPrinting';
import {useDispatch, useSelector} from 'react-redux';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  analysisColumns,
  buildTree,
  EXCLUDED_FIELDS,
  focusNextOutside,
  getFirstParentWithChildren,
  getParentIds,
} from '../batchPrintUtils';
import {useEffect, useMemo, useRef, useState} from 'react';
import {GridColDef} from '@mui/x-data-grid';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {Search} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {
  setAnalysis,
  setAnalysisSkipFolderToggle,
  setAnalysisDecision,
  setAnalysisSelectedSkipFolder,
  setAnalysisTranslationAction,
  setAnalysisTranslationSkipOrIgnoreDocuments,
  setSaveBatchPrint,
} from '../../../redux/slice/batchPrintSlice';
import classNames from 'classnames';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import { GridColumnHeaderParams } from '@mui/x-data-grid';

const shortInfo = () => (
  <>
    <p data-testid={`analysis-step1`} tabIndex={0}>
      {t('batchPrint.analysisGroup.tooltip.step1')}
    </p>
    <p data-testid={`analysis-step2`} tabIndex={0}>
      {t('batchPrint.analysisGroup.tooltip.step2')}
    </p>
    <ul data-testid={`analysis-step3-step4`} tabIndex={0}>
      <li data-testid={`analysis-step3`}>
        {t('batchPrint.analysisGroup.tooltip.step3')}
      </li>
      <li data-testid={`analysis-step4`} tabIndex={0}>
        {t('batchPrint.analysisGroup.tooltip.step4')}
      </li>
    </ul>
    <p data-testid={`analysis-step5`} tabIndex={0}>
      {t('batchPrint.analysisGroup.tooltip.step5')}
    </p>
    <ul data-testid={`analysis-step6-step7`} tabIndex={0}>
      <li data-testid={`analysis-step6`}>
        {t('batchPrint.analysisGroup.tooltip.step6')}
      </li>
      <li data-testid={`analysis-step7`} tabIndex={0}>
        {t('batchPrint.analysisGroup.tooltip.step7')}
      </li>
    </ul>
  </>
);

const treeInfo = () => (
  <>
    <p data-testid={`analysis-step1-text`} tabIndex={0}>
      {t('batchPrint.analysisGroup.treeTooltip.step1')}
    </p>
    <p data-testid={`analysis-step2-text`} tabIndex={0}>
      {t('batchPrint.analysisGroup.treeTooltip.step2')}
    </p>
  </>
);

const radioOptions = [
  {
    value: 'natives',
    label: t('batchPrint.analysisGroup.natives'),
  },
  {
    value: 'translated',
    label: t('batchPrint.analysisGroup.translated'),
  },
  {
    value: 'skip',
    label: t('batchPrint.analysisGroup.skip'),
  },
  {
    value: 'ignore',
    label: t('batchPrint.analysisGroup.ignore'),
  },
];

const getProductionKeys = (row: any): string[] => {
  return Object.keys(row).filter((key) => key.includes('#') && row[key] === 1);
};

const buildDefaultIssueDocuments = (rows: any[], printIssueCode: number) => {
  return rows?.map((row) => {
    const productionKeys = getProductionKeys(row);
    const isSkipped = row.SKIP_PRINTING === 1;

    // auto-select production ONLY if exactly one exists
    const selectedProduction =
      !isSkipped && productionKeys.length === 1 ? productionKeys[0] : 0;

    return {
      documentID: row.DOCID,
      isSkipped: isSkipped || productionKeys.length !== 1,
      printIssueCode,
      value: selectedProduction,
    };
  });
};

const updateIssueDocument = (
  issueDocuments: any[],
  updatedDoc: {
    documentID: number;
    isSkipped: boolean;
    printIssueCode: number;
    value: number | string;
  }
) => {
  const index = issueDocuments.findIndex(
    (d) => d.documentID === updatedDoc.documentID
  );

  if (index > -1) {
    const updated = [...issueDocuments];
    updated[index] = updatedDoc;
    return updated;
  }

  return [...issueDocuments, updatedDoc];
};

const translationLanguageOptions = [{id: 1, name: 'English - United States'}];

const Analysis = () => {
  const dispatch = useDispatch();
  const batchPrint = useSelector((state: any) => state.batchPrint);
  const saveBatchPrint = useSelector(
    (state: any) => state.batchPrint.saveBatchPrint.batchPrintDTO
  );

  const selectedProductionList =
    batchPrint?.basisForPrinting?.selectedProductionList;

  const productionIDs: string[] = selectedProductionList?.map(
    (data) => data?.id?.toString() ?? ''
  );
  const primaryKey = 'DOCID';

  const sourceType = batchPrint?.selectedSourceActionType;
  const selectedSource = batchPrint?.selectedSource;
  const analysisSkipperFolder = batchPrint?.analysis?.selectedSkippedFolder;

  const folderSkippedToggle = batchPrint?.analysis?.skipFolderToggle;
  const decisions = batchPrint?.analysis?.decisions ?? {};
  const printIssueSettingCode =
    batchPrint?.basisForPrinting?.printIssueSettingCode;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const toolTipRef = useRef<HTMLDivElement | null>(null);
  const isEntryResetFocusRef = useRef<HTMLDivElement | null>(null); 
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
 

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const [defaultExpandedParentIds, setDefaultExpandedParentIds] = useState<
    string[]
  >(['-1g']);

  const [gridRows, setGridRows] = useState<any>([]);

  const [action, setAction] = useState(
    batchPrint?.analysis?.translationAction || 'natives'
  );
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] =
    useState(translationLanguageOptions[0]);

  const sourceContextType =
    sourceType === 'search'
      ? 'SAVEDSEARCHES'
      : sourceType === 'tag'
        ? 'TAGS'
        : 'FOLDERS';

  const payload = {
    page: 1,
    id: Number(selectedSource),
    sourceContextTypeCode: sourceContextType,
    isProductionPrint: printIssueSettingCode === 'IGNOREPRINT',
    productionIDs: productionIDs?.filter((id) => id.trim() !== '') ?? [],
  };

  const {data: getAnalysisData} = useGetAnalysis(payload);
  const {data: getFoldersData} = useGetFolders();

  const skipPrintingColumn: GridColDef = {
    field: 'skipPrinting',
    headerName: t('batchPrint.analysisGroup.grid.skipPrinting'),
    flex: 1,
    sortable: false,
    renderHeader: (param: GridColumnHeaderParams) => (
                <div
                  tabIndex={0}
                  role="rowheader"
                  className="table_header"
                  data-testid={`keyword-highlight-list-columns-color-header`}
                >
                  {param.colDef.headerName}
                </div>
              ),
    renderCell: (params) => {
      const rowId = params.row[primaryKey];
      const selected = decisions[rowId]?.action === 'SKIP';

      return (
        <ArrRadio
          dataTestId={`skip-printing-radio-${rowId}`}
          id={`skip-printing-radio-${rowId}`}
          name={`radio-${rowId}`}
          value={selected ? 'SKIP' : ''}
          data={[{value: 'SKIP', label: ''}]}
          size="xs"
          className="row-radio"
          onChange={() => {
            dispatch(
              setAnalysisDecision({
                docId: rowId,
                action: 'SKIP',
              })
            );
            const updatedDoc = {
              documentID: rowId,
              isSkipped: true,
              printIssueCode: 0,
              value: 0, // SKIP default value
            };

            const updatedIssueDocuments = updateIssueDocument(
              saveBatchPrint.issueDocuments || [],
              updatedDoc
            );

            dispatch(
              setSaveBatchPrint({issueDocuments: updatedIssueDocuments})
            );
          }}
        />
      );
    },
  };

  useEffect(() => {
    setGridRows(getAnalysisData?.batchPrintAnalysisData?.analysisDetails);

    const defaultIssueDocuments = buildDefaultIssueDocuments(
      getAnalysisData?.batchPrintAnalysisData?.analysisDetails as [],
      1
    );

    dispatch(
      setSaveBatchPrint({
        issueDocuments: defaultIssueDocuments,
      })
    );
    dispatch(
      setSaveBatchPrint({
        folderIDForIssueSkippedDocuments:
          (getAnalysisData?.batchPrintAnalysisData?.decisionDocCount ?? 0) === 0
            ? 0
            : (sourceType === 'folder' ? Number(selectedSource) : 0) ||
              batchPrint?.analysis?.selectedSkippedFolder,
      })
    );

    const newAnalysis = {
      ...batchPrint.analysis,
      printTotalDocCount:
        getAnalysisData?.batchPrintAnalysisData?.printTotalDocCount,
      noIssuesDocCount:
        getAnalysisData?.batchPrintAnalysisData?.noIssuesDocCount,
      notAnyProductionDocCount:
        getAnalysisData?.batchPrintAnalysisData?.notAnyProductionDocCount,
      moreProducitonDocCount:
        getAnalysisData?.batchPrintAnalysisData?.moreProducitonDocCount,
      selectedSkippedFolder:
        (sourceType === 'folder' ? selectedSource : '') ||
        batchPrint?.analysis?.selectedSkippedFolder,
      decisionDocCount:
        getAnalysisData?.batchPrintAnalysisData?.decisionDocCount,
    };

    dispatch(
      setAnalysis({
        ...batchPrint.analysis,
        ...newAnalysis,
      })
    );
  }, [getAnalysisData?.batchPrintAnalysisData?.analysisDetails]);

  const focusOrder = () => [isEntryResetFocusRef.current, toolTipRef.current, toggleRef.current].filter(Boolean) as HTMLElement[];
 const handleContainerFocusCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key !== "Tab") return;

  const wrapper = containerRef.current;
  if (!wrapper) return;

  const order = focusOrder();
  const active = document.activeElement as HTMLElement | null;
  const idx = order.findIndex((el) => el === active);

  const atLast = idx === order.length - 1;


  // TAB on last -> move focus outside (next)
  if (!e.shiftKey && atLast) {
    e.preventDefault();
    focusNextOutside(wrapper, false);
    return;
  }

  e.preventDefault();

  const nextIdx = e.shiftKey ? (idx - 1 + order.length) % order.length : (idx + 1) % order.length;

  order[nextIdx].focus();
 }

 const handleToggleCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab' && e.shiftKey){
      const nextBtn = document.querySelector('[data-testid="next-page-btn"]') as HTMLButtonElement;
      const prevBtn = document.querySelector('[data-testid="previous-page-btn"]') as HTMLButtonElement;
        e.preventDefault();
        if(nextBtn.disabled){
          prevBtn?.focus();
        }else {
           nextBtn?.focus();
        }  
    }
    if (e.key === "Tab") return;
 }

   

  const paginatedRows = useMemo(() => {
    
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return gridRows?.slice(start, end);
   
  }, [gridRows, currentPage, itemsPerPage]);

  const getTreeDataList =
    useMemo((): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      const treeData = buildTree(getFoldersData?.folderTree || []);
      if (sourceType === 'folder') {
        setDefaultExpandedParentIds(batchPrint?.expandedParentIds);
      } else if (treeData && treeData.length > 0) {
        const firstParentWithChildren = getFirstParentWithChildren(
          treeData,
          null
        );
        setDefaultExpandedParentIds([firstParentWithChildren]);
      }
      return treeData;
    }, [getFoldersData]);

  useEffect(() => {
    if (!gridRows?.length) return;

    gridRows.forEach((row) => {
      const docId = row[primaryKey];

      if (!decisions[docId]) {
        dispatch(
          setAnalysisDecision({
            docId,
            action: 'SKIP',
          })
        );
      }
    });
  }, [gridRows, decisions, dispatch]);

  const dynamicFields = useMemo(() => {
    if (!paginatedRows?.length) return [];

    return Object.keys(paginatedRows[0]).filter(
      (key) =>
        key.includes('#') &&
        !EXCLUDED_FIELDS.includes(key) &&
        !key.startsWith('DOC')
    );
  }, [paginatedRows]);

  const dynamicRadioColumns = useMemo<GridColDef[]>(() => {
    return dynamicFields.map((field) => {
      const header = field.split('#')[0];
      const productionValue = field.split('#')[1];

      return {
        field,
        headerName: header,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const rowId = params.row[primaryKey];

          // only render radio if backend says "1"
          if (params.row[field] !== 1) return null;

          const selected =
            decisions[rowId]?.action === 'PRODUCTION' &&
            decisions[rowId]?.productionKey === field;

          return (
            <ArrRadio
              dataTestId={`${field}-radio-${rowId}`}
              id={`${field}-radio-${rowId}`}
              name={`radio-${rowId}`}
              value={selected ? field : ''}
              data={[{value: field, label: ''}]}
              size="xs"
              className="row-radio"
              onChange={() => {
                dispatch(
                  setAnalysisDecision({
                    docId: rowId,
                    action: 'PRODUCTION',
                    productionKey: field,
                  })
                );
                const updatedDoc = {
                  documentID: rowId,
                  isSkipped: false,
                  printIssueCode: 0,
                  value: Number(productionValue),
                };

                const updatedIssueDocuments = updateIssueDocument(
                  saveBatchPrint.issueDocuments || [],
                  updatedDoc
                );

                dispatch(
                  setSaveBatchPrint({issueDocuments: updatedIssueDocuments})
                );
              }}
            />
          );
        },
      };
    });
  }, [dynamicFields, decisions, dispatch]);

  const updatedColumns = useMemo<GridColDef[]>(() => {
    const cols = [...analysisColumns];

    const docIdIndex = cols.findIndex((col) => col.field === 'DOC_ID');

    if (docIdIndex !== -1) {
      cols.splice(
        docIdIndex + 1,
        0,
        skipPrintingColumn,
        ...dynamicRadioColumns
      );
    }

    return cols;
  }, [analysisColumns, dynamicRadioColumns]);

  const handleToggleChange = (status: boolean) => {
    dispatch(setAnalysisSkipFolderToggle(status));
  };

  const onPageChangeHandler = (value: number) => {
    setCurrentPage(value);
  };

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchKeyword(searchInput.trim());
    }
  };

  const handleSelectedItem = (value: string[] | null) => {
    dispatch(setAnalysisSelectedSkipFolder(value?.[0] ?? ''));
    dispatch(
      setSaveBatchPrint({
        folderIDForIssueSkippedDocuments: Number(value?.[0]) ?? 0,
      })
    );

    if (printIssueSettingCode === 'SKIPPRINTBUTFOLDER') {
      dispatch(setAnalysisTranslationSkipOrIgnoreDocuments(value ?? []));
    }

    if (Array.isArray(value) && value.length > 0 && !value[0].includes('g')) {
      const parentIds = getParentIds(getTreeDataList, value[0]);
      setDefaultExpandedParentIds(parentIds);
    }
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAction = event.target.value;
    setAction(selectedAction);
    dispatch(setAnalysisTranslationAction(selectedAction));
    dispatch(setAnalysisTranslationSkipOrIgnoreDocuments([]));
  };

  const handleDropdownChange = (value: any) => {
    setSelectedTranslationLanguage(value);
  };

  const isRadioEnabled =
    (getAnalysisData?.batchPrintAnalysisData?.translationDocumentCount ?? 0) >
    0;

  return (
    <Box className="analysis-container">
      <Box className="analysis-header ">
        <Typography
          className="typo-text subhead2_size"
          data-testid={`analysis-title`}
          tabIndex={0}
          aria-label={t('batchPrint.analysisGroup.title')}
        >
          {t('batchPrint.analysisGroup.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.analysisGroup.title')}
          closeTooltipOnEscPress={true}
          trigger={
            <InfoIcon
              aria-label='info-icon'
              fontSize="small"
              className="info-icon"
              data-testid="add-user-title-info"
              tabIndex={0}
            />
          }
        >
          {shortInfo()}
        </ArrTooltipWrapper>
      </Box>
      {printIssueSettingCode === 'SKIPPRINTBUTFOLDER' ? (
        // Analysis Viewable file variant
        <Box
          className="source-selection-body"
          data-testid="skip-print-but-folder-display-text"
        >
          <Typography
            className="typo-text body2_size analysis-description"
            data-testid={`analysis-skip-print-if-any`}
            tabIndex={0}
          >
            {t('batchPrint.analysisGroup.skipPrintIfAny')}
          </Typography>
          <Typography
            className="typo-text subhead4_size"
            data-testid={`analysis-content-title`}
            tabIndex={0}
          >
            {t('batchPrint.analysisGroup.contentTitle')}
          </Typography>
          <Typography
            className="typo-text body2_size"
            data-testid={`analysis-typography`}
            tabIndex={0}
          >
            {' '}
            {`${t('batchPrint.analysisGroup.requestTo')} ${
              getAnalysisData?.batchPrintAnalysisData?.printNativeTotalDocument
            } ${t('batchPrint.analysisGroup.documents')}.`}
          </Typography>
          <Typography
            className="typo-text body2_size analysis-description"
            data-testid={`analysis-typography-data-text`}
            tabIndex={0}
          >
            {(getAnalysisData?.batchPrintAnalysisData
              ?.translationDocumentCount ?? 0) > 0
              ? `${t('batchPrint.analysisGroup.skipPrintOfThese')}
              ${getAnalysisData?.batchPrintAnalysisData?.printNativeTotalDocument}
              ${t('batchPrint.analysisGroup.documents')},            
              ${getAnalysisData?.batchPrintAnalysisData?.noFileLocationDocumentCount}
              ${t('batchPrint.analysisGroup.skipPrintDoNotHaveFiles')}
              ${t('batchPrint.analysisGroup.skipPrintDocsWithNoFiles')}
              ${t('batchPrint.analysisGroup.thereAre')}
              ${getAnalysisData?.batchPrintAnalysisData?.translationDocumentCount}
              ${t('batchPrint.analysisGroup.skipPrintHaveOneOrMore')}
              ${t('batchPrint.analysisGroup.skipPrintYouMustSelect')}
              ${getAnalysisData?.batchPrintAnalysisData?.translationDocumentCount}
              ${t('batchPrint.analysisGroup.documents')}.

            `
              : `${t('batchPrint.analysisGroup.noneTranslated')}`}
          </Typography>
          {(getAnalysisData?.batchPrintAnalysisData?.translationDocumentCount ??
            0) > 0 && (
            <Box tabIndex={0}>
              <RadioGroup value={action} onChange={handleActionChange}>
                {radioOptions.map(({value, label}, index) => {
                  const isSelected = action === value;

                  return (
                    <Box key={value} data-testid="action-radio-group-container">
                      <FormControlLabel
                        data-testid={`action-radio-label-${value}`}
                        key={value}
                        value={value}
                        control={
                          <Radio
                            slotProps={{
                              input: {
                                'data-testid': `radio-${value}`,
                              } as React.InputHTMLAttributes<HTMLInputElement>,
                            }}
                            disabled={false}
                            icon={
                              (getAnalysisData?.batchPrintAnalysisData
                                ?.translationDocumentCount ?? 0) > 0 ? (
                                <RadioEnableIcon
                                  data-testid={`action-radio-icon-enabled-${value}`}
                                />
                              ) : (
                                <RadioInactiveIcon
                                  data-testid={`action-radio-icon-disabled-${value}`}
                                />
                              )
                            }
                            checkedIcon={
                              <RadioActiveIcon
                                data-testid={`action-radio-active-icon--${value}`}
                              />
                            }
                          />
                        }
                        label={
                          <span
                            className={classNames(
                              'bulk-radio-label typo-text body3_size',
                              {
                                disabled: !isRadioEnabled,
                                active: isSelected && isRadioEnabled,
                                inactive: !isSelected && isRadioEnabled,
                              }
                            )}
                            data-testid={`analysis-label-${index}`}
                          >
                            {label}
                          </span>
                        }
                      />
                      {value === 'translated' && (
                        <Box className="dropdown" tabIndex={0}>
                          <ArrSingleSelectDropdown
                            dropdownLabel={t(
                              'batchPrint.basisForPrinting.selectProductionSet'
                            )}
                            dropdownItems={translationLanguageOptions?.map(
                              (i) => ({
                                id: i.id,
                                name: i.name,
                              })
                            )}
                            selectedValues={selectedTranslationLanguage}
                            onSelectionChange={handleDropdownChange}
                            sizeVariant="medium"
                            dataTestId={`asd-analysis-access-control-${label}`}
                            selectRestProps={{
                              id: 'translation-language-dropdown',
                              name: 'translation-language-dropdown',
                            }}
                            tabIndex={1}
                          />
                        </Box>
                      )}
                      {(value === 'skip' || value === 'ignore') &&
                        isSelected && (
                          <Box className="tree-search-container">
                            <Box className="tree-header" tabIndex={0}>
                              <ArrTextField
                                dataTestId={`analysis-batch-print-source-selection-${label}`}
                                state="enabled"
                                sizeVariant="medium"
                                className="search-input"
                                placeholder={t(
                                  `batchPrint.sourceSelection.searchPlaceholder`
                                )}
                                aria-label={t(
                                  `batchPrint.sourceSelection.searchPlaceholder`
                                )}
                                data-testid={`search-input-analysis-${index}`}
                                value={searchInput}
                                onChange={(e) => {
                                  setSearchInput(e.target.value);
                                  if (e.target.value.trim() === '') {
                                    setSearchKeyword('');
                                  }
                                }}
                                onKeyDown={handleSearchKeyEnter}
                                slotProps={{
                                  inputLabel: {shrink: false},
                                  input: {
                                    endAdornment: (
                                      <InputAdornment
                                        position="end"
                                        className={
                                          searchKeyword.trim() === ''
                                            ? 'search-adornment-icon'
                                            : 'clear-adornment-icon'
                                        }
                                        onClick={() => {
                                          if (searchKeyword.trim() !== '') {
                                            setSearchInput('');
                                            setSearchKeyword('');
                                          }
                                        }}
                                        data-testid={`analysis-input-adornment-${label}`}
                                      >
                                        {searchKeyword.trim() === '' ? (
                                          <Search />
                                        ) : (
                                          <CancelIcon className="clear-icon" />
                                        )}
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                              />
                            </Box>
                            <Box className="tree-container" tabIndex={0}>
                              <ArrTreeView
                                setSelectedItem={handleSelectedItem}
                                itemsList={getTreeDataList ?? []}
                                selectedItem={
                                  batchPrint.analysis
                                    .translationSkipOrIgnoreDocuments[0]
                                }
                                handleNoResultMessage={t(
                                  'common.noResultFound'
                                )}
                                multipleSelection={false}
                                showActions={false}
                                isShowCountLabel={true}
                                showTooltip
                                searchKeyword={searchKeyword}
                                defaultExpandedIds={defaultExpandedParentIds}
                                isSingleSelectionOnCtrl={true}
                                isBlockParentNodeSelection={true}
                                expandOnSearch={true}
                                isShowCheckbox={true}
                                expandSelect={false}
                                checkboxGroupSelection={false}
                              />
                            </Box>
                          </Box>
                        )}
                    </Box>
                  );
                })}
              </RadioGroup>
            </Box>
          )}
        </Box>
      ) : (
        // Analysis Prior Productions
        <Box
          className="source-selection-body"
          data-testid="analysis-prior-productions-display-text"
        >
          <Typography
            className="typo-text body2_size analysis-description"
            data-testid={`analysis-description`}
            tabIndex={0}
          >
            {t('batchPrint.analysisGroup.description')}
          </Typography>
          <Box className="body-container">
            <Typography
              className="typo-text subhead4_size"
              data-testid={`analysis-content-title-text`}
              tabIndex={0}
            >
              {t('batchPrint.analysisGroup.contentTitle')}
            </Typography>
            <Typography
              className="typo-text body2_size"
              data-testid={`analysis-typography-totalt-request-to-doc-count`}
              tabIndex={0}
            >
              {' '}
              {`${t('batchPrint.analysisGroup.requestTo')} ${
                getAnalysisData?.batchPrintAnalysisData?.printTotalDocCount
              } ${t('batchPrint.analysisGroup.documents')}.`}
            </Typography>
            <Typography
              className="typo-text body2_size"
              data-testid={`analysis-typography-no-issues-totalDoc`}
              tabIndex={0}
            >
              {`${t('batchPrint.analysisGroup.noIssues')} ${
                (getAnalysisData?.batchPrintAnalysisData?.printTotalDocCount ??
                  0) !==
                getAnalysisData?.batchPrintAnalysisData?.noIssuesDocCount
                  ? getAnalysisData?.batchPrintAnalysisData?.noIssuesDocCount
                  : 'any'
              } ${t('batchPrint.analysisGroup.documents')}.`}
            </Typography>

            {(getAnalysisData?.batchPrintAnalysisData?.printTotalDocCount ??
              0) > 0 &&
              (getAnalysisData?.batchPrintAnalysisData?.printTotalDocCount ??
                0) !==
                getAnalysisData?.batchPrintAnalysisData?.noIssuesDocCount && (
                <Box>
                  <Typography
                    className="typo-text body2_size"
                    data-testid={`analysis-however`}
                    tabIndex={0}
                  >
                    {t('batchPrint.analysisGroup.however')}
                  </Typography>
                  {(getAnalysisData?.batchPrintAnalysisData
                    ?.noFileLocationDocumentCount ?? 0) > 0 && (
                    <Typography
                      className="typo-text body2_size"
                      data-testid={`analysis-typography-icon`}
                      tabIndex={0}
                    >
                      {' '}
                      {`${t('batchPrint.analysisGroup.ofThese')} ${
                        getAnalysisData?.batchPrintAnalysisData
                          ?.printTotalDocCount
                      } ${t('batchPrint.analysisGroup.documents')},  ${
                        getAnalysisData?.batchPrintAnalysisData
                          ?.noFileLocationDocumentCount
                      }  ${t('batchPrint.analysisGroup.noFileToPrint')}`}
                    </Typography>
                  )}
                  <Typography
                    className="typo-text body2_size decision-text"
                    data-testid={`analysis-typography-link`}
                    tabIndex={0}
                  >
                    {' '}
                    {`${t('batchPrint.analysisGroup.thereAre')} ${
                      getAnalysisData?.batchPrintAnalysisData?.decisionDocCount
                    } ${t('batchPrint.analysisGroup.documentNeedDecision')} ${
                      getAnalysisData?.batchPrintAnalysisData
                        ?.printTotalDocCount
                    } `}
                  </Typography>
                  <Typography
                    className="typo-text body2_size"
                    data-testid={`analysis-typography-input`}
                    tabIndex={0}
                  >
                    {` ${t('batchPrint.analysisGroup.documents')}:`}
                  </Typography>
                  <Box className="list-content" tabIndex={0}>
                    <li
                      className="typo-text body2_size"
                      data-testid={`analysis-li`}
                    >
                      {' '}
                      {`${
                        getAnalysisData?.batchPrintAnalysisData
                          ?.notAnyProductionDocCount
                      } ${t('batchPrint.analysisGroup.notInAnyProduction')}`}
                    </li>
                    <li
                      className="typo-text body2_size"
                      data-testid={`analysis-li-btn`}
                    >
                      {' '}
                      {`${
                        getAnalysisData?.batchPrintAnalysisData
                          ?.moreProducitonDocCount
                      } ${t('batchPrint.analysisGroup.moreThanOneProduction')}`}
                    </li>
                  </Box>
                </Box>
              )}
          </Box>
        </Box>
      )}
      {(getAnalysisData?.batchPrintAnalysisData?.decisionDocCount ?? 0) > 0 && (
        <>
          <Box className="analysis-grid-container" tabIndex={0}>
            <ArrDataGrid
              data-testid="analysis-data-grid"
              rows={paginatedRows ?? []}
              // hideFooter={isNoData}
              className={'analysis-data-grid'}
              columns={updatedColumns}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              onPageChange={onPageChangeHandler}
              showPerPageOptions={false}
              rowCount={
                getAnalysisData?.batchPrintAnalysisData?.analysisDetails?.length
              }
              primaryKey="DOCID"
              columnHeaderHeight={66}
              entityType={'entries'}
              pageSize={itemsPerPage}
              style={{
                height:
                  paginatedRows?.length * 40 +
                  48 +
                  (gridRows?.length > 10 ? 56 : 0),
              }}
              hideFooter={gridRows?.length <= 10}
            />
          </Box>
          <Box className="tree-search-container">
            <Box
              className="toggle-header"
              tabIndex={0}
              ref={containerRef}
              onKeyDownCapture={handleContainerFocusCapture}
            >
              <Box>
                <ArrToggle
                  value={folderSkippedToggle}
                  size="medium"
                  onChange={(e) => handleToggleChange(e.target.checked)}
                  dataTestId="action-analysis-toggle"
                  slotProps={{
                    input: {
                      'aria-checked': folderSkippedToggle,
                      'aria-label': 'action-analysis-toggle',
                      role: 'switch',
                      'ref': ((el) => toggleRef.current = el),
                       
                    },
                  }}
                  onCustomKeyDown= {(e: any) => handleToggleCapture(e)}
                  
                />
              </Box>
              <Box className="name-tooltip-header" >
                <Typography
                  className="typo-text subhead4_size"
                  data-testid={`analysis-folder-skipped`}
                  tabIndex={0}
                  ref={isEntryResetFocusRef}
                >
                  {t('batchPrint.analysisGroup.folderSkipped')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('batchPrint.analysisGroup.title')}
                  closeTooltipOnEscPress={true}
                  trigger={
                    <InfoIcon
                      fontSize="small"
                      className="info-icon"
                      data-testid="add-user-title-info"
                      tabIndex={0}
                      ref={toolTipRef}
                    />
                  }
                >
                  {treeInfo()}
                </ArrTooltipWrapper>
              </Box>
            </Box>
            {folderSkippedToggle && (
              <>
                <Box className="tree-header" tabIndex={0}>
                  <ArrTextField
                    state="enabled"
                    sizeVariant="medium"
                    className="search-input"
                    placeholder={t(
                      `batchPrint.sourceSelection.searchPlaceholder`
                    )}
                    aria-label={t(
                      `batchPrint.sourceSelection.searchPlaceholder`
                    )}
                    dataTestId="analysis-header-search-input"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      if (e.target.value.trim() === '') {
                        setSearchKeyword('');
                      }
                    }}
                    onKeyDown={handleSearchKeyEnter}
                    onClear={() => {
                      setSearchInput('');
                      setSearchKeyword('');
                    }}
                    slotProps={{
                      inputLabel: {shrink: false},
                      input: {
                        inputRef,
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            className={
                              searchKeyword.trim() === ''
                                ? 'search-adornment-icon'
                                : 'clear-adornment-icon'
                            }
                            onClick={() => {
                              if (searchKeyword.trim() !== '') {
                                setSearchInput('');
                                setSearchKeyword('');
                              }
                            }}
                            data-testid="analysis-input-adornment-search"
                          >
                            {searchInput.trim() === '' ? (
                              <Search />
                            ) : (
                              
                                <CancelIcon
                                  className="clear-icon"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    setSearchInput('');
                                    setSearchKeyword('');
                                  }}
                                  onKeyDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (
                                      e.key === 'Enter' ||
                                      e.code === 'Space'
                                    ) {
                                      setSearchInput('');
                                      setSearchKeyword('');
                                      requestAnimationFrame(() => {
                                        inputRef.current?.focus();
                                      });
                                    }
                                  }}
                                  />
                            )}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
                <Box className="tree-container" tabIndex={0}>
                  <ArrTreeView
                    setSelectedItem={handleSelectedItem}
                    itemsList={getTreeDataList ?? []}
                    selectedItem={analysisSkipperFolder}
                    handleNoResultMessage={t('common.noResultFound')}
                    multipleSelection={false}
                    showActions={false}
                    isShowCountLabel={true}
                    showTooltip
                    searchKeyword={searchKeyword}
                    defaultExpandedIds={defaultExpandedParentIds}
                    isSingleSelectionOnCtrl={true}
                    isBlockParentNodeSelection={true}
                    expandOnSearch={true}
                    isShowCheckbox={true}
                    expandSelect={false}
                    checkboxGroupSelection={false}
                  />
                </Box>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Analysis;
